import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../context/auth-context'
import { MeetingHrFeedbackResult, MeetingStatus, HrManageMeetingInput, AppraisalWithFeedbackDto, MeetingPdfStatus } from '../../../types'
import { PerModal } from '../../../components/custom_components/Modal'
import { useDateTime } from '../../../shared/useDateTime'
import { appraisalTableUiProps } from './appraisalTable.uiprops'
import { getConclusionHrefByMeeting } from '../../meeting/util/hrefUtils'
import PerText from '../../../components/custom_components/Text'
import { PerCardInfo } from '../../../components/custom_components/CardInfo'

interface Props {
  open: boolean
  resolveFeedbackInProgress: boolean
  modalData: AppraisalWithFeedbackDto
  onCancel: () => void
  onSubmit: (formData: { employeeId: string; hrManageMeetingInput: HrManageMeetingInput }) => void
  modalError?: string
}

export const ConfirmedAppraisalFeedbackModal: FC<Props> = ({
  open,
  onCancel,
  onSubmit,
  modalData,
  resolveFeedbackInProgress,
  modalError,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort, formatDateTimeShort } = useDateTime(user)
  const [isSubmitting, toggleSubmitting] = useState<boolean>(false)
  const { feedbackColor, feedbackHeading } = appraisalTableUiProps(t, formatDateShort, formatDateTimeShort)
  const hrFeedbackIsOpen = modalData.hrResult === MeetingHrFeedbackResult.NoResult
  const meetingStatus: MeetingStatus = modalData.status

  return (
    <PerModal
      title={{
        heading: hrFeedbackIsOpen
          ? t('companySettingsView.appraisalTab.table.feedback.modal.heading.review')
          : t('companySettingsView.appraisalTab.table.feedback.modal.heading.resolved'),
        subheading: t('companySettingsView.appraisalTab.table.feedback.modal.heading.subheading', {
          employee: `${modalData.replyer.firstName} ${modalData.replyer.lastName}`,
          date: formatDateShort(modalData.date),
          reviewer: '',
        }),
      }}
      size="large"
      visible={open}
      onCancel={onCancel}
      footer={{
        cancelBtn: {
          onClick: () => onCancel(),
        },
        customBtns: [
          {
            visible: !!modalData.conclusionPdf,
            text: t('companySettingsView.appraisalTab.table.feedback.modal.btnConclusion'),
            color: 'primary',
            icon: 'view_fill',
            asLink: modalData.conclusionPdf
              ? getConclusionHrefByMeeting({
                  id: modalData.id,
                  conclusionPdfFileName: modalData.conclusionPdf.fileName,
                  conclusionPdfDownloadToken: modalData.conclusionPdf.downloadToken,
                  conclusionPdfStatus: modalData.conclusionPdf.status,
                })
              : undefined,
            disabled:
              !modalData.conclusionPdf ||
              !modalData.conclusionPdf.status ||
              modalData.conclusionPdf.status !== MeetingPdfStatus.FinishedSuccess,
          },
          {
            visible: hrFeedbackIsOpen,
            disabled: resolveFeedbackInProgress,
            isProcessing: { active: isSubmitting },
            text: t('companySettingsView.appraisalTab.table.feedback.modal.btnReadAndResolve'),
            color: 'success',
            icon: 'view_fill',
            onClick: () => {
              toggleSubmitting(true)
              onSubmit({
                employeeId: modalData.replyer.id,
                hrManageMeetingInput: {
                  meetingId: modalData.id,
                  hrResult: MeetingHrFeedbackResult.Resolved,
                },
              })
              toggleSubmitting(false)
            },
          },
        ],
      }}
    >
      <PerCardInfo heading={feedbackHeading(meetingStatus, modalData.replyer.firstName)} icon={{ color: feedbackColor(meetingStatus) }}>
        <PerText appearance="quote">{modalData.hrFeedback}</PerText>
        {modalError && (
          <>
            <PerText color="error" fontSize="m" mt="m">
              {modalError}
            </PerText>
          </>
        )}
      </PerCardInfo>
    </PerModal>
  )
}
